/**
 * EntryScreenRulesSection Component
 *
 * Destination section for configuring entry screen rules based on URL parameters.
 * Rules override launchScreenId at runtime: first matching rule wins.
 * Fallback to default start screen when no rules match.
 *
 * @module webplacements/components/editors/destinations
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Switch,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type {
  EntryScreenRule,
  ParameterDefinition,
} from '@modules/webplacements/types/webplacements.types';

// ============================================================================
// TYPES
// ============================================================================

export interface EntryScreenRulesSectionProps {
  /** Current entry screen rules */
  rules: EntryScreenRule[];
  /** Called when rules change */
  onChange: (rules: EntryScreenRule[]) => void;
  /** Defined URL params (for paramKey dropdown — the whitelist) */
  urlParams: ParameterDefinition[];
  /** Available V2 step nodes for target screen dropdown */
  availableScreens: Array<{ id: string; name: string }>;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

interface NewRuleState {
  paramKey: string;
  value: string;
  targetScreenId: string;
}

const initialNewRule: NewRuleState = {
  paramKey: '',
  value: '',
  targetScreenId: '',
};

// ============================================================================
// COMPONENT
// ============================================================================

export const EntryScreenRulesSection: React.FC<EntryScreenRulesSectionProps> = ({
  rules,
  onChange,
  urlParams,
  availableScreens,
  disabled = false,
}) => {
  const theme = useTheme();
  const [isAdding, setIsAdding] = useState(false);
  const [newRule, setNewRule] = useState<NewRuleState>(initialNewRule);

  // Available param keys from urlParams whitelist
  const paramKeys = urlParams.map((p) => p.sourceDetails?.urlParam || p.key);

  // --- Handlers ---

  const handleAddClick = useCallback(() => {
    setIsAdding(true);
    setNewRule(initialNewRule);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setNewRule(initialNewRule);
  }, []);

  const handleSaveNew = useCallback(() => {
    if (!newRule.paramKey || !newRule.value.trim() || !newRule.targetScreenId) return;

    const targetScreen = availableScreens.find((s) => s.id === newRule.targetScreenId);
    if (!targetScreen) return;

    const rule: EntryScreenRule = {
      id: crypto.randomUUID(),
      paramKey: newRule.paramKey,
      operator: 'eq',
      value: newRule.value.trim(),
      targetScreenId: targetScreen.id,
      targetScreenName: targetScreen.name,
      isActive: true,
    };

    onChange([...rules, rule]);
    setIsAdding(false);
    setNewRule(initialNewRule);
  }, [newRule, availableScreens, rules, onChange]);

  const handleDelete = useCallback(
    (index: number) => {
      onChange(rules.filter((_, i) => i !== index));
    },
    [rules, onChange]
  );

  const handleToggleActive = useCallback(
    (index: number) => {
      const updated = rules.map((r, i) =>
        i === index ? { ...r, isActive: !r.isActive } : r
      );
      onChange(updated);
    },
    [rules, onChange]
  );

  // --- Render ---

  return (
    <Box data-testid="wp-section-entry-screen-rules">
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PlayArrowIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
        <Typography variant="body2" color="text.secondary">
          Select which screen the funnel starts from based on URL parameters
        </Typography>
        <Tooltip title="Entry screen rules let you start the funnel on different screens for different campaigns. Rules are evaluated top-to-bottom; first match wins. If no rule matches, the default start screen is used. Saved user progress takes priority over rules.">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {!isAdding && (
          <Button
            data-testid="wp-entry-rule-add-btn"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={disabled || paramKeys.length === 0 || availableScreens.length === 0}
          >
            Add Rule
          </Button>
        )}
      </Box>

      {/* Rules Table */}
      {rules.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '25%' }}>URL Parameter</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Value</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '5%' }} />
                <TableCell sx={{ fontWeight: 600, width: '30%' }}>Start Screen</TableCell>
                <TableCell align="right" sx={{ width: '20%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule, index) => (
                <TableRow
                  key={rule.id}
                  hover
                  data-testid={`wp-entry-rule-row-${index}`}
                  sx={{ opacity: rule.isActive ? 1 : 0.5 }}
                >
                  <TableCell>
                    <Chip
                      label={rule.paramKey}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`= "${rule.value}"`}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <ArrowForwardIcon fontSize="small" color="action" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.targetScreenName || rule.targetScreenId}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title={rule.isActive ? 'Active' : 'Inactive'}>
                        <Switch
                          data-testid={`wp-entry-rule-toggle-${index}`}
                          size="small"
                          checked={rule.isActive}
                          onChange={() => handleToggleActive(index)}
                          disabled={disabled}
                        />
                      </Tooltip>
                      <IconButton
                        data-testid={`wp-entry-rule-delete-${index}`}
                        size="small"
                        onClick={() => handleDelete(index)}
                        disabled={disabled}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add New Rule Form */}
      {isAdding && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* URL Param Key */}
          <FormControl size="small" fullWidth required>
            <InputLabel>URL Parameter</InputLabel>
            <Select
              data-testid="wp-entry-rule-param-select"
              value={newRule.paramKey}
              onChange={(e) =>
                setNewRule((prev) => ({ ...prev, paramKey: e.target.value }))
              }
              label="URL Parameter"
            >
              {paramKeys.map((key) => (
                <MenuItem key={key} value={key}>
                  <Typography variant="body2" fontFamily="monospace">
                    {key}
                  </Typography>
                </MenuItem>
              ))}
              {paramKeys.length === 0 && (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    Define URL parameters in Sources first
                  </Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Expected Value */}
          <TextField
            data-testid="wp-entry-rule-value-input"
            label="Expected Value"
            size="small"
            fullWidth
            required
            value={newRule.value}
            onChange={(e) =>
              setNewRule((prev) => ({ ...prev, value: e.target.value }))
            }
            placeholder="e.g. summer, facebook, promo_123"
            helperText={newRule.paramKey
              ? `Match when ?${newRule.paramKey}=${newRule.value || '...'}`
              : 'Select a URL parameter first'}
          />

          {/* Target Screen */}
          <FormControl size="small" fullWidth required>
            <InputLabel>Start Screen</InputLabel>
            <Select
              data-testid="wp-entry-rule-screen-select"
              value={newRule.targetScreenId}
              onChange={(e) =>
                setNewRule((prev) => ({ ...prev, targetScreenId: e.target.value }))
              }
              label="Start Screen"
            >
              {availableScreens.map((screen) => (
                <MenuItem key={screen.id} value={screen.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{screen.name}</Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {screen.id.substring(0, 8)}...
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button data-testid="wp-entry-rule-cancel-btn" size="small" onClick={handleCancelAdd}>
              Cancel
            </Button>
            <Button
              data-testid="wp-entry-rule-save-btn"
              size="small"
              variant="contained"
              onClick={handleSaveNew}
              disabled={!newRule.paramKey || !newRule.value.trim() || !newRule.targetScreenId}
            >
              Add
            </Button>
          </Box>
        </Box>
      )}

      {/* Empty State */}
      {rules.length === 0 && !isAdding && (
        <Box
          sx={{
            p: 2,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No entry screen rules configured.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {paramKeys.length === 0
              ? 'Define URL parameters in Sources section first, then create rules.'
              : availableScreens.length === 0
                ? 'Link a V2 funnel with screens to create entry screen rules.'
                : 'Add rules to start the funnel on different screens based on URL parameter values.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EntryScreenRulesSection;
